import React, { useState, useRef, useEffect } from 'react';
import { useMessagingStore } from '../../stores/messagingStore';
import { useTranslation } from 'react-i18next';
import { getNonEnglishCharacters } from '../../utils/englishValidator';

interface MessageInputProps {
    conversationId: string;
    onSend: (content: string) => Promise<void>;
}

const MessageInput: React.FC<MessageInputProps> = ({ conversationId, onSend }) => {
    const { t } = useTranslation('messaging');
    const [message, setMessage] = useState('');
    const [showEnglishHint, setShowEnglishHint] = useState(false);
    const [nonEnglishChars, setNonEnglishChars] = useState<string[]>([]);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const { sendingMessage, error, startTyping, stopTyping } = useMessagingStore();

    // Auto-resize textarea: start at 40px (single line), grow up to 120px
    useEffect(() => {
        if (textareaRef.current) {
            textareaRef.current.style.height = '40px';
            const scrollH = textareaRef.current.scrollHeight;
            if (scrollH > 40) {
                textareaRef.current.style.height = `${Math.min(scrollH, 120)}px`;
            }
        }
    }, [message]);

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const text = e.target.value;

        // Check for non-English characters
        const badChars = getNonEnglishCharacters(text);
        setNonEnglishChars(badChars);
        setShowEnglishHint(badChars.length > 0);

        // Only update message if it's English
        // (or allow all characters but show warning)
        setMessage(text);

        // Handle typing indicator
        if (text.length > 0) {
            startTyping(conversationId);

            // Clear previous timeout
            if (typingTimeoutRef.current) {
                clearTimeout(typingTimeoutRef.current);
            }

            // Stop typing indicator after 3 seconds of no input
            typingTimeoutRef.current = setTimeout(() => {
                stopTyping(conversationId);
            }, 3000);
        } else {
            stopTyping(conversationId);
        }
    };

    const handleSend = async () => {
        const trimmedMessage = message.trim();

        if (!trimmedMessage || sendingMessage) return;

        // Validate English-only
        const badChars = getNonEnglishCharacters(trimmedMessage);
        if (badChars.length > 0) {
            setNonEnglishChars(badChars);
            setShowEnglishHint(true);
            return;
        }

        try {
            await onSend(trimmedMessage);
            setMessage('');
            setShowEnglishHint(false);
            stopTyping(conversationId);

            // Clear typing timeout
            if (typingTimeoutRef.current) {
                clearTimeout(typingTimeoutRef.current);
            }
        } catch (err) {
            // Error is handled in store
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        // Send on Enter (without Shift)
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    // Cleanup typing timeout on unmount
    useEffect(() => {
        return () => {
            if (typingTimeoutRef.current) {
                clearTimeout(typingTimeoutRef.current);
            }
        };
    }, []);

    return (
        <div className="border-t border-gray-200 bg-white p-4 flex-shrink-0" style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}>
            {/* English-only Hint */}
            {showEnglishHint && (
                <div className="mb-3 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-sm">
                    <span className="text-amber-700">
                        {nonEnglishChars.length > 0
                            ? t('englishOnly.hintWithChars', { chars: nonEnglishChars.join(' ') })
                            : t('englishOnly.hint')
                        }
                    </span>
                </div>
            )}

            {/* Error Message */}
            {error && (
                <div className="mb-3 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
                    {error}
                </div>
            )}

            {/* Input Area */}
            <div className="flex items-end gap-3">
                <div className="flex-1 relative">
                    <textarea
                        ref={textareaRef}
                        value={message}
                        onChange={handleChange}
                        onKeyDown={handleKeyDown}
                        placeholder={t('typeMessage')}
                        rows={1}
                        className={`
                            w-full px-4 py-[10px] pr-12 bg-gray-100 rounded-2xl resize-none
                            focus:outline-none focus:ring-2 focus:ring-primary-500 focus:bg-white
                            placeholder:text-gray-400 text-base leading-5
                            ${showEnglishHint ? 'ring-2 ring-amber-400' : ''}
                        `}
                        disabled={sendingMessage}
                    />
                </div>

                {/* Send Button */}
                <button
                    onClick={handleSend}
                    disabled={!message.trim() || sendingMessage || showEnglishHint}
                    className={`
                        flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center
                        transition-colors
                        ${message.trim() && !showEnglishHint && !sendingMessage
                            ? 'bg-primary-500 hover:bg-primary-600 text-white'
                            : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        }
                    `}
                >
                    {sendingMessage ? (
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-5 w-5"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                        >
                            <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" />
                        </svg>
                    )}
                </button>
            </div>

            {/* Character count hint */}
            <p className="mt-1 text-xs text-gray-400 text-right">
                {t('pressEnterToSend')}
            </p>
        </div>
    );
};

export default MessageInput;
